import { useContext, useEffect, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Select,
  Radio,
  message,
  Divider,
  Spin,
  Result,
  InputNumber,
  Table,
  Tooltip,
} from "antd";
import MaskedInput from "antd-mask-input";
import { HJAddressSelectBox, HJSearchSelect } from "@FORMS";
import { OptionSelect } from "@MASTER";
import { AppContext, AuthContext } from "@CONTEXTS";
import { axios } from "@LIBS";
import { Constants } from "@CONFIG";
import { useHistory } from "react-router-dom";
import { moment } from "@LIBS";
import { AiFillCaretRight } from "react-icons/ai";
import { FaRegTimesCircle } from "react-icons/fa";
import ReceiverInfoModal from "../delivery/receiver-info-modal";
import ProductModal from "../delivery/product-modal";
import { TiContacts } from "react-icons/ti";
import { ProductEndpoints, getProductsByCodes } from "@PRODUCTS";
import { ReceiverEndpoints } from "@RECEIVERS";
import {
  getCustomField,
  mapProductsForApi,
  addProductToDeliveryForm,
  applyReceiverToForm,
  removeProductFromDeliveryForm,
} from "@HELPERS";

const { TextArea } = Input;
const { Option } = Select;

const renderTruncatedText = (text, maxLength = 40) => {
  if (text && text.length > maxLength) {
    return (
      <Tooltip title={text} placement="topLeft">
        {`${text.substring(0, maxLength)}...`}
      </Tooltip>
    );
  }
  return text;
};

const Returned = ({ record: initialRecord }) => {
  const [record, setRecord] = useState(initialRecord);
  const [form] = Form.useForm();
  const history = useHistory();
  const [cityId, setCityId] = useState(null);
  const [townId, setTownId] = useState(null);
  const [step, setStep] = useState("FORM");
  const [barcode, setBarcode] = useState(null);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [desi, setDesi] = useState(null);
  const [deliveryType, setDeliveryType] = useState("HX_STD");
  const { options } = useContext(AppContext);
  const { info } = useContext(AuthContext);
  const [receiverInfoModal, setReceiverInfoModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const isDisabled = step === "SELECT_DATE" || Boolean(record);

  const desiOptionName =
    deliveryType === "TMH" ? "DESI_RETURNED_TMH" : "DESI_RETURNED";

  const returnedDeliveryTypeOptions = [
    ...(options?.DESI_RETURNED?.length
      ? [{ value: "HX_STD", label: "Standart Teslimat" }]
      : []),
    ...(options?.DESI_RETURNED_TMH?.length
      ? [{ value: "TMH", label: "XL Gönderi" }]
      : []),
  ];

  const matchRecordProducts = async () => {
    if (!info?.productSelection || !record?.deliveryContents?.length) {
      return;
    }

    const skus = [
      ...new Set(
        record.deliveryContents
          .map((item) => String(item?.sku || "").trim())
          .filter(Boolean)
      ),
    ];
    if (!skus.length) {
      return;
    }

    const catalog = (await getProductsByCodes(skus)) || [];
    const productsByCode = new Map(
      catalog.map((product) => [
        String(product?.code || "")
          .trim()
          .toLocaleLowerCase("tr"),
        product,
      ])
    );
    const matchedProducts = [];
    const unmatchedSkus = [];

    record.deliveryContents.forEach((item) => {
      const sku = String(item?.sku || "").trim();
      if (!sku) {
        return;
      }

      const product = productsByCode.get(sku.toLocaleLowerCase("tr"));
      const quantity = Number(item?.quantity) > 0 ? Number(item.quantity) : 1;

      if (product) {
        const mappedProduct = {
          ...product,
          id: product.id || product._id,
        };
        for (let i = 0; i < quantity; i += 1) {
          matchedProducts.push(mappedProduct);
        }
      } else if (!unmatchedSkus.includes(sku)) {
        unmatchedSkus.push(sku);
      }
    });

    if (matchedProducts.length) {
      form.setFieldsValue({
        products: matchedProducts,
        ...(info?.specialTags?.includes("B2BPRO") && {
          totalParcels: matchedProducts.length,
        }),
      });
    }

    if (unmatchedSkus.length) {
      message.warning(
        `${unmatchedSkus.join(
          ", "
        )} kodlu malzemeler kayıtlı ürünlerde bulunamadı`
      );
    }
  };

  const setDefaultFormValues = (shouldResetFields = false) => {
    if (shouldResetFields) {
      form.resetFields();
      setCityId(null);
      setTownId(null);
      setDates([]);
    }

    const defaultDeliveryType = record?.serviceTypes?.includes("TMH")
      ? "TMH"
      : options?.DESI_RETURNED?.length
      ? "HX_STD"
      : options?.DESI_RETURNED_TMH?.length
      ? "TMH"
      : "HX_STD";
    const defaultDesiOptionName =
      defaultDeliveryType === "TMH" ? "DESI_RETURNED_TMH" : "DESI_RETURNED";
    const defaultDesi = options?.[defaultDesiOptionName]?.[0]?.key;

    setDeliveryType(defaultDeliveryType);
    setDesi(defaultDesi);

    const defaultValues = {
      totalParcels: 1,
      deliveryType: defaultDeliveryType,
      desi: defaultDesi,
      products: [],
    };

    if (options["WAREHOUSES"]?.length === 1) {
      defaultValues.warehouse = options["WAREHOUSES"][0].key;
    }

    if (record?.receiver) {
      const nameParts = (record.receiver?.receiver || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      const digits = String(record.receiver?.receiverGsm || "").replace(
        /\D/g,
        ""
      );
      const phoneDigits =
        digits.length === 11 && digits.startsWith("0")
          ? digits.slice(1)
          : digits;

      const recordDesi = Number(
        record.delivery?.hjDesi ?? record.delivery?.desi
      );
      const matchedDesi = (options?.[defaultDesiOptionName] || []).find(
        (item) => {
          const [min, max] = String(item?.tr || "")
            .split("-")
            .map((part) => Number(String(part).trim()));

          return (
            !Number.isNaN(min) &&
            !Number.isNaN(max) &&
            recordDesi >= min &&
            recordDesi <= max
          );
        }
      );

      Object.assign(defaultValues, {
        firstName: nameParts[0] || null,
        lastName: nameParts.slice(1).join(" ") || null,
        phone:
          phoneDigits.length === 10
            ? `${phoneDigits.slice(0, 3)} ${phoneDigits.slice(
                3,
                6
              )} ${phoneDigits.slice(6, 8)} ${phoneDigits.slice(8, 10)}`
            : phoneDigits || null,
        addressLine: record.receiver?.address || null,
        city: record.receiver?.city || null,
        town: record.receiver?.town || null,
        district: record.receiver?.district || null,
        desi: matchedDesi?.key ?? defaultDesi,
        totalParcels: record.delivery?.totalParcels ?? 1,
        customerDeliveryNo:record.delivery?.customerOrderId || null,
      });

      if (defaultDeliveryType === "TMH" && !Number.isNaN(recordDesi)) {
        defaultValues.manuelDesi = recordDesi;
      }

      const matchedWarehouse = (options["WAREHOUSES"] || []).find(
        (item) =>
          String(item?.name || "")
            .trim()
            .toLocaleLowerCase("tr") ===
          String(record.warehouseName || "")
            .trim()
            .toLocaleLowerCase("tr")
      );
      if (matchedWarehouse?.key) {
        defaultValues.warehouse = matchedWarehouse.key;
      }

      setDesi(defaultValues.desi);
    }

    form.setFieldsValue(defaultValues);
    matchRecordProducts();
  };

  useEffect(() => {
    setDefaultFormValues();
  }, [options, form, record]);

  const handleValidateAndContinue = () => {
    form.validateFields().then(async (values) => {
      form.setFieldsValue({ returnedDate: null });
      setLoading(true);
      const response = await axios({
        url: `${Constants.BACKEND_API_URL}/api/deliveries/returned/days?city=${values.city.name}&town=${values.town.name}`,
        method: "GET",
      });
      setLoading(false);
      if (response?.data) {
        const stupidArr = response.data[0].towns[0].xDock[0].days;
        if (!stupidArr) {
          return message.error({
            content:
              "Seçtiğiniz adres için uygun bir randevu tarihi bulunamamıştır",
            className: "app-error-message",
          });
        }
        const dateList = stupidArr?.map((item) => item.date);
        setDates(dateList);
        setStep("SELECT_DATE");

        if (info?.autoAdvanceReturnedDate) {
          form.setFieldsValue({ returnedDate: dateList[0] });
          handleSave();
        }
      } else {
        message.error({
          content:
            "Seçtiğiniz adres için uygun bir randevu tarihi bulunamamıştır",
          className: "app-error-message",
        });
      }
    });
  };

  const handleSave = () => {
    form.validateFields().then(async (values) => {
      setLoading(true);
      const formData = new FormData();

      formData.append("customerDeliveryNo", values.customerDeliveryNo || null);
      formData.append(
        "deliveryType",
        values.deliveryType || deliveryType || null
      );
      formData.append("desi", values.desi || null);
      formData.append("manuelDesi", values.manuelDesi || null);
      formData.append("totalParcels", values.totalParcels || null);
      formData.append("warehouse", values.warehouse || null);
      formData.append("returnedDate", values.returnedDate || null);
      formData.append("products", mapProductsForApi(values.products));
      if (record?.delivery?.customerDeliveryNo) {
        formData.append("relatedBarcode", record.delivery.customerDeliveryNo);
      }

      formData.append(
        "sender",
        JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone.replace(/ /g, ""),
          city: values.city.name,
          town: values.town.name,
          district: values.district?.name || null,
          addressLine: values.addressLine,
        })
      );

      if (!info?.specialTags?.includes("B2BPRO")) {
        formData.append(
          "recipient",
          JSON.stringify({
            fullName: values.recipientFullName,
            phone: values.recipientPhone.replace(/ /g, ""),
          })
        );
      }

      if (info?.customFields) {
        const customFields = info?.customFields?.filter((x) =>
          x?.deliveryType?.includes("RETURNED_DELIVERY")
        );

        const cfx = [];

        for (let i = 0; i < customFields.length; i++) {
          const customItem = customFields[i];

          cfx.push({
            name: customItem.name,
            value: values[customItem.name],
          });

          if (customItem.type === "FILE_UPLOADER") {
            if (
              (!values[customItem.name] ||
                values[customItem.name]?.length < 1 ||
                !values[customItem.name]?.[0]?.originFileObj) &&
              customItem.required
            ) {
              setLoading(false);
              message.error({
                content: `${customItem.label} için geçersiz dosya.`,
                className: "app-error-message",
              });
              return;
            }

            formData.append(
              customItem.name,
              values[customItem.name]?.[0]?.originFileObj
            );
          }
        }

        if (cfx.length > 0) {
          formData.append("customFields", JSON.stringify(cfx));
        }
      }

      const response = await axios({
        url: `${Constants.BACKEND_API_URL}/api/deliveries/returned/create`,
        method: "POST",
        data: formData,
      });

      if (response?.success) {
        setBarcode(response?.data?.customerDeliveryNo);
        setStep("SUCCESS");
      }

      setLoading(false);
    });
  };
  const openReceiverInfoModal = async () => {
    if (!info.authorities.includes("RECEIVER_LIST")) {
      return message.error("Kişileri görüntüleme yetkiniz bulunmamaktadır!");
    }
    setReceiverInfoModal(true);
  };

  return (
    <Spin spinning={loading}>
      {receiverInfoModal && (
        <ReceiverInfoModal
          onClose={() => setReceiverInfoModal(false)}
          fieldName={null}
          form={form}
          isReturned={true}
        />
      )}
      {productModal && (
        <ProductModal
          onClose={() => setProductModal(false)}
          fieldName={null}
          form={form}
          isReturned={true}
          setDeliveryType={setDeliveryType}
          setDesi={setDesi}
        />
      )}
      <div style={{ width: 850, margin: "0 auto" }}>
        <Form form={form} layout="vertical">
          {step === "SUCCESS" ? (
            <Result
              status="success"
              title={`${
                barcode && barcode
              } nolu randevulu iade gönderisi başarıyla oluşturuldu!`}
              extra={
                record
                  ? null
                  : [
                      <Button
                        type="primary"
                        key="console"
                        onClick={() => history.push("/deliveries/operation")}
                      >
                        Operasyondaki Gönderilerim
                      </Button>,
                      <Button
                        key="buy"
                        onClick={() => {
                          setStep("FORM");
                          setDefaultFormValues(true);
                        }}
                      >
                        Yeniden Oluştur
                      </Button>,
                    ]
              }
            />
          ) : (
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="customerDeliveryNo"
                  label="Sipariş Numarası"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen sipariş numarası giriniz",
                    },
                    {
                      min: 5,
                      max: 16,
                      message:
                        "Lütfen geçerli bir sipariş numarası giriniz (5-16 karakter arası).",
                    },
                  ]}
                >
                  <Input disabled={step === "SELECT_DATE"} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="warehouse"
                  label="Varış Deposu"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen gönderinin geri geleceği depoyu seçiniz",
                    },
                  ]}
                >
                  <OptionSelect
                    name="WAREHOUSES"
                    labelKey={
                      info?.specialTags?.includes("B2BPRO") ? "name" : "tr"
                    }
                    disabled={step === "SELECT_DATE"}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children?.toLowerCase() ?? "").includes(
                        input?.toLowerCase()
                      )
                    }
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="deliveryType"
                  label="Teslimat Türü"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen teslimat türü seçiniz",
                    },
                  ]}
                >
                  <Select
                    disabled={isDisabled}
                    options={returnedDeliveryTypeOptions}
                    onChange={(val) => {
                      const nextDesiOptionName =
                        val === "TMH" ? "DESI_RETURNED_TMH" : "DESI_RETURNED";
                      const nextDesi = options?.[nextDesiOptionName]?.[0]?.key;
                      setDeliveryType(val);
                      setDesi(nextDesi);
                      form.setFieldsValue({
                        deliveryType: val,
                        desi: nextDesi,
                        manuelDesi: null,
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="desi"
                  label="Desi Aralığı"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen desi seçiniz",
                    },
                  ]}
                >
                  <OptionSelect
                    key={desiOptionName}
                    name={desiOptionName}
                    disabled={step === "SELECT_DATE"}
                    onChange={(val) => {
                      let tempForm = form.getFieldsValue();
                      tempForm["manuelDesi"] = null;
                      form.setFieldsValue(tempForm);
                      setDesi(val);
                    }}
                  />
                </Form.Item>
              </Col>
              {deliveryType === "TMH" && (
                <Col span={12}>
                  <Form.Item
                    name="manuelDesi"
                    label="Desi Bilgisi"
                    rules={[
                      {
                        required: true,
                        message: "Lütfen desi bilgisini giriniz",
                      },
                      () => ({
                        validator(_, value) {
                          if (
                            value === null ||
                            value === undefined ||
                            value === ""
                          ) {
                            return Promise.resolve();
                          }

                          const [min, max] = (
                            options[desiOptionName]?.filter(
                              (x) => x.key === desi
                            )?.[0]?.tr || ""
                          )
                            .split("-")
                            .map((part) => Number(String(part).trim()));

                          if (!Number.isNaN(min) && Number(value) < min) {
                            return Promise.reject(
                              `Desi değeri en az ${min} olmalıdır`
                            );
                          }

                          if (!Number.isNaN(max) && Number(value) > max) {
                            return Promise.reject(
                              `Desi değeri en fazla ${max} olabilir`
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      disabled={step === "SELECT_DATE"}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={12}>
                <Form.Item
                  name="totalParcels"
                  label="Parça Sayısı"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen seçim yapınız",
                    },
                  ]}
                >
                  <Select
                    disabled={
                      step === "SELECT_DATE" ||
                      (info?.productSelection &&
                        info?.specialTags?.includes("B2BPRO"))
                    }
                    showSearch
                  >
                    {Array.from(
                      {
                        length:
                          deliveryType === "TMH" ||
                          info?.specialTags?.includes("B2B") ||
                          info?.specialTags?.includes("B2BPRO")
                            ? 60
                            : 10,
                      },
                      (_, i) => i + 1
                    ).map((item) => (
                      <Option key={item} value={item}>
                        {item}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {info?.customFields &&
                info?.customFields?.length > 0 &&
                info?.customFields
                  .filter((x) => x.deliveryType.includes("RETURNED_DELIVERY"))
                  .map((item) => (
                    <Col
                      span={item.name === "FILE_UPLOADER" ? 24 : 12}
                      key={item.name}
                    >
                      {getCustomField({ item })}
                    </Col>
                  ))}

              {info?.productSelection && (
                <Col span={24}>
                  <Divider style={{ marginTop: 0 }} />
                  <h4 style={{ marginTop: 4 }}>Malzeme / Ürün Seçimi:</h4>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ flex: 1, maxWidth: 420 }}>
                      <HJSearchSelect
                        url={ProductEndpoints.listProducts}
                        placeholder="Kod veya ürün adı ile malzeme ara"
                        emptyText="Kayıtlı malzeme/ürün bulunamadı."
                        disabled={isDisabled}
                        renderOption={(row) =>
                          `${row?.code || ""} ${row?.name || ""}`.trim()
                        }
                        onSelect={(record) =>
                          addProductToDeliveryForm({
                            form,
                            fieldName: null,
                            record,
                            desiOptions: options["DESI_RETURNED_TMH"],
                            setDeliveryType,
                            setDesi,
                            syncTotalParcels:
                              info?.specialTags?.includes("B2BPRO"),
                          })
                        }
                      />
                    </div>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => setProductModal(true)}
                      icon={<TiContacts />}
                      disabled={isDisabled}
                      style={{ margin: 0, padding: 0 }}
                    >
                      Kayıtlı Malzeme / Ürünlerim
                    </Button>
                  </div>

                  <Form.Item shouldUpdate noStyle>
                    {() => {
                      const selectedProducts =
                        form.getFieldValue("products") || [];
                      const totalDesi = selectedProducts.reduce(
                        (sum, product) => sum + Number(product?.desi || 0),
                        0
                      );

                      return selectedProducts.length > 0 ? (
                        <Table
                          size="small"
                          style={{ marginBottom: 10 }}
                          columns={[
                            {
                              title: "Kod",
                              dataIndex: "code",
                              key: "code",
                              ellipsis: true,
                              render: (code) => renderTruncatedText(code),
                            },
                            {
                              title: "Ürün",
                              dataIndex: "name",
                              key: "name",
                              ellipsis: true,
                              render: (name) => renderTruncatedText(name),
                            },
                            {
                              title: "Desi",
                              dataIndex: "desi",
                              key: "desi",
                              width: 80,
                            },
                            {
                              title: "",
                              key: "action",
                              width: 48,
                              align: "center",
                              render: (_, __, index) => (
                                <Button
                                  icon={
                                    <FaRegTimesCircle
                                      style={{ margin: 0, top: 2 }}
                                    />
                                  }
                                  type="text"
                                  danger
                                  disabled={step === "SELECT_DATE"}
                                  onClick={() => {
                                    removeProductFromDeliveryForm({
                                      form,
                                      fieldName: null,
                                      index,
                                      desiOptions: options["DESI_RETURNED_TMH"],
                                      setDeliveryType,
                                      setDesi,
                                      syncTotalParcels:
                                        info?.specialTags?.includes("B2BPRO"),
                                    });
                                  }}
                                />
                              ),
                            },
                          ]}
                          dataSource={selectedProducts}
                          rowKey={(record, index) =>
                            `${record?.id || record?._id}-${index}`
                          }
                          pagination={false}
                          scroll={{ y: 300 }}
                          summary={() => (
                            <Table.Summary fixed>
                              <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={2}>
                                  <b>Toplam Desi</b>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                  <b>{totalDesi}</b>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} />
                              </Table.Summary.Row>
                            </Table.Summary>
                          )}
                        />
                      ) : null;
                    }}
                  </Form.Item>

                  <Form.Item
                    name="products"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (
                            info?.productSelectionRequired &&
                            (!value || value.length === 0)
                          ) {
                            return Promise.reject(
                              "Lütfen malzeme/ürün seçiniz!"
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <div />
                  </Form.Item>
                </Col>
              )}

              <Col span={24}>
                <Divider style={{ marginTop: 0 }} />
              </Col>
              <Col span={24}>
                <h4 style={{ marginTop: 4 }}>
                  Teslim Alınacak Kişi Bilgileri:
                </h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div style={{ flex: 1, maxWidth: 420 }}>
                    <HJSearchSelect
                      url={ReceiverEndpoints.listReceivers}
                      placeholder="Ad, soyad veya telefon ile alıcı ara"
                      emptyText="Kayıtlı alıcı bulunamadı."
                      disabled={isDisabled}
                      renderOption={(row) =>
                        `${row?.firstName || ""} ${row?.lastName || ""} ${
                          row?.phone || ""
                        }`.trim()
                      }
                      onSelect={(record) =>
                        applyReceiverToForm({
                          form,
                          fieldName: null,
                          record,
                        })
                      }
                    />
                  </div>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => openReceiverInfoModal()}
                    icon={<TiContacts />}
                    disabled={isDisabled}
                    style={{ margin: 0, padding: 0 }}
                  >
                    Kayıtlı Alıcı Bilgilerim
                  </Button>
                </div>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="firstName"
                  label="Adı:"
                  onKeyPress={(e) => {
                    if (!/[a-zA-Z wığüşöçĞÜŞÖÇİ]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  rules={[
                    {
                      required: true,
                      message: "Lütfen geçerli ad giriniz!",
                    },
                  ]}
                >
                  <Input maxLength={100} disabled={isDisabled} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="lastName"
                  label="Soyadı:"
                  onKeyPress={(e) => {
                    if (!/[a-zA-Z wığüşöçĞÜŞÖÇİ]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  rules={[
                    {
                      required: true,
                      message: "Lütfen geçerli soyad giriniz!",
                    },
                  ]}
                >
                  <Input maxLength={100} disabled={isDisabled} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="phone"
                  label="Telefon Numarası:"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen geçerli bir telefon numarası giriniz!",
                    },
                    () => ({
                      validator(_, value) {
                        if (!value || value.includes("_")) {
                          return Promise.reject(
                            new Error(
                              "Lütfen geçerli bir telefon numarası giriniz!"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <MaskedInput
                    mask="111 111 11 11"
                    size="10"
                    disabled={isDisabled}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Ev/İş Yeri Adresi:"
                  name="addressLine"
                  rules={[
                    {
                      required: true,
                      message: "Lütfen geçerli bir adres giriniz!",
                    },
                  ]}
                >
                  <TextArea
                    autoSize={{
                      minRows: 1,
                      maxRows: 3,
                    }}
                    maxLength={250}
                    disabled={isDisabled}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="İl:"
                  name="city"
                  rules={[
                    {
                      required: true,
                      type: "object",
                      message: "Lütfen il seçiniz",
                    },
                  ]}
                >
                  <HJAddressSelectBox
                    type="city"
                    disabled={isDisabled}
                    onChange={(val) => {
                      let tempForm = form.getFieldsValue();
                      let deliveryTypeVal = {
                        ...val,
                        deliveryType: deliveryType || "HX_STD",
                        desi: tempForm.desi,
                      };
                      setCityId(deliveryTypeVal);
                      setTownId(null);
                      // Kayıtlı alıcı / string→nesne eşlemesi forced göndermez; sadece kullanıcı seçimi forced: true.
                      if (val?.forced) {
                        form.setFieldsValue({
                          district: null,
                          town: null,
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="İlçe:"
                  name="town"
                  rules={[
                    {
                      required: true,
                      type: "object",
                      message: "Lütfen ilçe seçiniz",
                    },
                  ]}
                >
                  <HJAddressSelectBox
                    type="town"
                    disabled={isDisabled}
                    cityId={cityId}
                    onChange={(val) => {
                      let tempForm = form.getFieldsValue();
                      let deliveryTypeVal = {
                        ...val,
                        deliveryType: deliveryType || "HX_STD",
                        desi: tempForm.desi,
                      };
                      setTownId(deliveryTypeVal);
                      if (val?.forced) {
                        form.setFieldsValue({
                          district: null,
                        });
                      }
                    }}
                    deliveryType={deliveryType || "HX_STD"}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Mahalle:" name="district">
                  <HJAddressSelectBox
                    type="district"
                    townId={townId}
                    deliveryType={deliveryType || "HX_STD"}
                    disabled={isDisabled}
                  />
                </Form.Item>
              </Col>

              {!info?.specialTags?.includes("B2BPRO") && (
                <>
                  <Col span={16}>
                    <Form.Item
                      name="recipientFullName"
                      label="Teslim alacak kişinin adı soyadı:"
                      onKeyPress={(e) => {
                        if (!/[a-zA-Z wığüşöçĞÜŞÖÇİ]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      rules={[
                        {
                          required: true,
                          message: "Lütfen geçerli ad giriniz!",
                        },
                      ]}
                    >
                      <Input maxLength={100}  disabled={step === "SELECT_DATE"} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="recipientPhone"
                      label="Teslim alacak kişinin telefon numarası:"
                      rules={[
                        {
                          required: true,
                          message:
                            "Lütfen geçerli bir telefon numarası giriniz!",
                        },
                        () => ({
                          validator(_, value) {
                            if (!value || value.includes("_")) {
                              return Promise.reject(
                                new Error(
                                  "Lütfen geçerli bir telefon numarası giriniz!"
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <MaskedInput
                        mask="111 111 11 11"
                        size="10"
                        disabled={step === "SELECT_DATE"}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              {step === "FORM" && (
                <Col span={24}>
                  <div style={{ textAlign: "right" }}>
                    <Button
                      type="primary"
                      icon={<AiFillCaretRight />}
                      onClick={() => handleValidateAndContinue()}
                    >
                      Devam Et
                    </Button>
                  </div>
                </Col>
              )}
              {step === "SELECT_DATE" && (
                <>
                  <Divider />
                  <Col span={24}>
                    <Form.Item
                      label="İade Alınmasını İstediğiniz Tarihi Seçiniz:"
                      name="returnedDate"
                      rules={[
                        {
                          required: true,
                          message: "Lütfen bir tarih seçiniz.",
                        },
                      ]}
                    >
                      <Radio.Group
                        buttonStyle="solid"
                        size="large"
                        disabled={info?.autoAdvanceReturnedDate}
                      >
                        {dates.map((item) => (
                          <Radio.Button key={item} value={item}>
                            {moment(item, "YYYY-MM-DD").format("DD MMMM YYYY")}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                    <Divider />
                  </Col>
                  <Col flex="auto">
                    <Button onClick={() => setStep("FORM")}>Geri Dön</Button>
                  </Col>
                  <Col flex="none">
                    <Button
                      type="primary"
                      icon={<AiFillCaretRight />}
                      onClick={() => handleSave()}
                    >
                      Devam Et
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          )}
        </Form>
      </div>
    </Spin>
  );
};

export default Returned;
